import { useId } from 'react';

// Each referral rank's emblem: bronze, silver and gold medals on a ribbon, a diamond, a crown; a plain shield for a
// member with no rank yet. Drawn as SVG (viewBox 48) so it stays sharp at every size.
const METALS = {
  1: ['#ffd2a8', '#d98b4c', '#8f4a1c', '#5c2a0b'], // bronze: light, mid, dark, deep
  2: ['#ffffff', '#d4dae6', '#8b94a7', '#4f5668'], // silver
  3: ['#fff4b0', '#f6c445', '#c07d06', '#7a4a00'], // gold
} as const;

/** The emblem of rank `rank` (0 member … 5 Crown). `reached` false draws it dimmed, as a rank still ahead. */
export function RankEmblem({ rank, size = 32, reached = true, title }: { rank: number; size?: number; reached?: boolean; title?: string }) {
  const id = useId().replace(/:/g, '');
  const cls = `rank-emblem r${rank}${reached ? '' : ' locked'}`;
  const a11y = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true };
  return (
    <svg className={cls} viewBox="0 0 48 48" width={size} height={size} {...a11y} data-rank={rank}>
      {rank >= 1 && rank <= 3 ? <Medal id={id} metal={METALS[rank as 1 | 2 | 3]} /> : rank === 4 ? <Diamond id={id} /> : rank === 5 ? <Crown id={id} /> : <Shield id={id} />}
    </svg>
  );
}

function Medal({ id, metal }: { id: string; metal: readonly string[] }) {
  const [light, mid, dark, deep] = metal;
  return (
    <>
      <defs>
        <linearGradient id={`rb${id}`} x1="0" x2="1">
          <stop offset="0" stopColor="#7c3aed" />
          <stop offset="1" stopColor="#d946ef" />
        </linearGradient>
        <radialGradient id={`md${id}`} cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor={light} />
          <stop offset="0.45" stopColor={mid} />
          <stop offset="1" stopColor={dark} />
        </radialGradient>
        <linearGradient id={`rim${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={light} />
          <stop offset="0.5" stopColor={dark} />
          <stop offset="1" stopColor={mid} />
        </linearGradient>
      </defs>
      {/* the ribbon */}
      <path d="M14 2h8l6 14h-8z" fill={`url(#rb${id})`} />
      <path d="M34 2h-8l-6 14h8z" fill={`url(#rb${id})`} opacity="0.85" />
      <path d="M16 2h4l5 12h-4z" fill="#fff" opacity="0.18" />
      {/* the medal */}
      <circle cx="24" cy="30" r="15" fill={`url(#rim${id})`} />
      <circle cx="24" cy="30" r="12.2" fill={`url(#md${id})`} stroke={deep} strokeOpacity="0.45" strokeWidth="0.8" />
      <path
        d="M24 21.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z"
        fill={deep}
        opacity="0.55"
        transform="translate(0 .6)"
      />
      <path d="M24 21.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" fill={light} />
      <path d="M14.5 26a10.5 10.5 0 0 1 8-7" fill="none" stroke="#fff" strokeOpacity="0.7" strokeWidth="1.4" strokeLinecap="round" />
    </>
  );
}

function Diamond({ id }: { id: string }) {
  return (
    <>
      <defs>
        <linearGradient id={`dg${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e0f7ff" />
          <stop offset="0.45" stopColor="#38bdf8" />
          <stop offset="1" stopColor="#6d28d9" />
        </linearGradient>
      </defs>
      {/* the crown of the gem, then its pavilion in facets */}
      <path d="M12 9h24l9 11-21 24L3 20z" fill={`url(#dg${id})`} />
      <path d="M3 20h42" stroke="#e0f7ff" strokeOpacity="0.8" strokeWidth="1" />
      <path d="M12 9l5 11L24 9l7 11 5-11" fill="none" stroke="#e0f7ff" strokeOpacity="0.75" strokeWidth="1" strokeLinejoin="round" />
      <path d="M17 20l7 24 7-24" fill="none" stroke="#bae6fd" strokeOpacity="0.6" strokeWidth="1" strokeLinejoin="round" />
      <path d="M12 9l5 11H3z" fill="#fff" opacity="0.35" />
      <path d="M24 9l7 11H17z" fill="#fff" opacity="0.2" />
      <path d="M31 20l-7 24 21-24z" fill="#1e1b4b" opacity="0.28" />
      {/* a glint */}
      <path d="M39 4l1 2.6 2.6 1-2.6 1-1 2.6-1-2.6-2.6-1 2.6-1z" fill="#fff" />
    </>
  );
}

function Crown({ id }: { id: string }) {
  return (
    <>
      <defs>
        <linearGradient id={`cg${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff4b0" />
          <stop offset="0.5" stopColor="#f6c445" />
          <stop offset="1" stopColor="#b8730a" />
        </linearGradient>
        <radialGradient id={`gem${id}`} cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#fde8ff" />
          <stop offset="0.5" stopColor="#d946ef" />
          <stop offset="1" stopColor="#6d28d9" />
        </radialGradient>
      </defs>
      {/* five points over a band */}
      <path d="M5 16l9 8 5-13 5 9 5-9 5 13 9-8-4 20H9z" fill={`url(#cg${id})`} stroke="#7a4a00" strokeOpacity="0.5" strokeWidth="0.8" strokeLinejoin="round" />
      <rect x="8.5" y="35" width="31" height="6.5" rx="1.6" fill={`url(#cg${id})`} stroke="#7a4a00" strokeOpacity="0.5" strokeWidth="0.8" />
      <path d="M10 18.5l3.8 3.4" stroke="#fff" strokeOpacity="0.6" strokeWidth="1.2" strokeLinecap="round" />
      {/* the gems: on the tips and on the band */}
      <circle cx="5" cy="15" r="2.6" fill={`url(#gem${id})`} />
      <circle cx="19" cy="10" r="2.6" fill={`url(#gem${id})`} />
      <circle cx="29" cy="10" r="2.6" fill={`url(#gem${id})`} />
      <circle cx="43" cy="15" r="2.6" fill={`url(#gem${id})`} />
      <circle cx="24" cy="38.2" r="2.4" fill={`url(#gem${id})`} />
      <circle cx="15" cy="38.2" r="1.6" fill="#38bdf8" />
      <circle cx="33" cy="38.2" r="1.6" fill="#38bdf8" />
    </>
  );
}

function Shield({ id }: { id: string }) {
  return (
    <>
      <defs>
        <linearGradient id={`sh${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a78bfa" />
          <stop offset="1" stopColor="#4c1d95" />
        </linearGradient>
      </defs>
      <path d="M24 4l16 6v12c0 10.5-6.8 18.6-16 22-9.2-3.4-16-11.5-16-22V10z" fill={`url(#sh${id})`} opacity="0.85" />
      <path d="M24 9l11 4.2v8.6c0 7.6-4.6 13.5-11 16.2-6.4-2.7-11-8.6-11-16.2v-8.6z" fill="none" stroke="#ede9fe" strokeOpacity="0.55" strokeWidth="1.2" />
      <circle cx="24" cy="20" r="4" fill="#ede9fe" opacity="0.8" />
      <path d="M16.5 32c1.4-4 4.2-6 7.5-6s6.1 2 7.5 6" fill="none" stroke="#ede9fe" strokeOpacity="0.8" strokeWidth="2" strokeLinecap="round" />
    </>
  );
}
